import {
  DynamicField,
  FieldVariant,
  FileFieldProps,
  NumberFieldProps,
  SelectFieldProps,
  SelectOption,
  TextFieldProps,
} from '@/components/form-builder/form-builder.types';
import { DacRepository } from '@/store/dac-state/dac-state.repository';
import { DynaStatus, TemplateResponse } from '@/types';
import { FileSelectEvent } from 'primeng/fileupload';
import { Observable, of } from 'rxjs';

interface getDACFormObjectProps {
  store: DacRepository;
  marketType: SelectOption[];
  modePassationOptions?: SelectOption[];
  dynaDocsModelOptions?: SelectOption[];
  onMarketTypeChange?: (typeMarcheId?: number) => void;
  showFileUploadModal?: Observable<boolean>;
  showFileUploadSupportRefer?: Observable<boolean>;
  downloadTemplate?: () => void;
  removeTemplate?: () => void;
  // Returns the Observable so onFileSelect below can subscribe to it - it used to be called
  // and its return value discarded, which meant the upload (an HTTP POST wrapped in an
  // Observable) never actually fired: nothing ever subscribed to it. The file only reached
  // the backend later, incidentally, when Save re-uploaded whatever was still sitting in
  // getPendingTemplateFile(). That also meant the "template uploaded" toast and the
  // placeholders-scan-on-upload hook (see ManageDocumentDynamicsComponent.uploadTemplate)
  // never ran for an existing (edit-mode) document.
  uploadTemplate?: (file: File) => Observable<TemplateResponse>;
  downloadGuide?: () => void;
  removeGuide?: () => void;
  uploadGuide?: (file: File) => Observable<TemplateResponse>;
  isEditMode?: Observable<boolean>;
}

export const getDACFormObject = ({
  store,
  marketType,
  modePassationOptions = [],
  dynaDocsModelOptions = [],
  onMarketTypeChange,
  downloadTemplate,
  removeTemplate,
  uploadTemplate,
  downloadGuide,
  uploadGuide,
  isEditMode,
}: getDACFormObjectProps) => {
  const nameField: DynamicField<TextFieldProps> = {
    label: 'dynamicDocument.form.name',
    variant: FieldVariant.TEXT,
    isRequired: true,
    props: {
      value: store.getNestedObservable<string>('dto', 'name'),
      onChange: (value: string) => {
        store.setNested('dto', 'name', value);
      },
    },
  };

  const orderField: DynamicField<NumberFieldProps> = {
    label: 'dynamicDocument.form.order',
    variant: FieldVariant.NUMBER,
    isRequired: true,
    props: {
      value: store.getNestedObservable<number>('dto', 'dynaDocOrder'),
      onChange: (value: string) => {
        store.setNested('dto', 'dynaDocOrder', Number(value));
      },
    },
  };

  const marketTypeField: DynamicField<SelectFieldProps> = {
    label: 'dynamicDocument.form.marketType',
    variant: FieldVariant.SELECT,
    isRequired: true,
    props: {
      value: store.getObservable<SelectOption>('typeMarcheIdSelected'),
      onSelectChange: (value: SelectOption) => {
        store.setNested('typeMarcheIdSelected', value);
        store.setNested('dto', 'typeMarcheId', value.code);
        // reset mode passation when market type changes
        store.set('modePassationIdSelected', { name: '', code: undefined });
        store.setNested('dto', 'modePassationId', undefined);
        onMarketTypeChange?.(value?.code as number);
      },
      options: marketType || [],
    },
  };

  const modePassationField: DynamicField<SelectFieldProps> = {
    label: 'dynamicDocument.form.modePassation',
    variant: FieldVariant.SELECT,
    isRequired: true,
    props: {
      value: store.getObservable<SelectOption>('modePassationIdSelected'),
      onSelectChange: (value: SelectOption) => {
        store.setNested('modePassationIdSelected', value);
        store.setNested('dto', 'modePassationId', value?.code as number);
      },
      options: modePassationOptions,
    },
  };

  const statusField: DynamicField<SelectFieldProps> = {
    label: 'dynamicDocument.form.status',
    variant: FieldVariant.SELECT,
    isRequired: true,
    props: {
      value: store.getObservable<SelectOption>('statusIdSelected'),
      onSelectChange: (value: SelectOption) => {
        store.setNested('statusIdSelected', value);
        store.setNested('dto', 'status', value.code);
      },
      options: [
        { name: 'Actif', code: DynaStatus.ACTIF },
        { name: 'Inactif', code: DynaStatus.NON_ACTIF },
      ],
    },
  };

  const staticField: DynamicField<SelectFieldProps> = {
    label: 'dynamicDocument.form.isStatic',
    variant: FieldVariant.SELECT,
    isDisabled: isEditMode,
    props: {
      value: store.getObservable<SelectOption>('isStaticSelected'),
      onSelectChange: (value: SelectOption) => {
        store.set('isStaticSelected', value);
        store.setNested('dto.isStatic', value.code === 'true', true);
      },
      options: [
        { name: 'Statique', code: 'true' },
        { name: 'Dynamique', code: 'false' },
      ],
    },
  };

  const isAAOField: DynamicField<SelectFieldProps> = {
    label: 'dynamicDocument.form.isAAO',
    variant: FieldVariant.SELECT,
    isRequired: true,
    props: {
      value: store.getObservable<SelectOption>('isAAOSelected'),
      onSelectChange: (value: SelectOption) => {
        store.set('isAAOSelected', value);
        store.setNested('dto.isAAO', value.code === 'true', true);
      },
      options: [
        { name: 'Oui', code: 'true' },
        { name: 'Non', code: 'false' },
      ],
    },
  };

  const dynaDocsModelField: DynamicField<SelectFieldProps> = {
    label: 'dynamicDocument.form.dynaDocsModel',
    variant: FieldVariant.SELECT,
    isRequired: true,
    props: {
      value: store.getObservable<SelectOption>('dynaDocsModelIdSelected'),
      onSelectChange: (value: SelectOption) => {
        store.set('dynaDocsModelIdSelected', value);
        store.setNested('dto', 'dynaDocsModelId', value?.code as number);
      },
      options: dynaDocsModelOptions,
    },
  };

  const templateFile = store.get<TemplateResponse>('templateMetadata');

  const templateField: DynamicField<FileFieldProps> = {
    label: 'dynamicDocument.form.templatePick',
    variant: FieldVariant.FILE,
    description: 'dynamicDocument.form.templatePickDescription',
    isRequired: true,
    isHidden: store.getNestedObservable<boolean>('dto', 'isStatic'),
    props: {
      mode: 'advanced',
      accept: '.docx',
      multiple: false,
      existingFileHeader: false,
      existingLabel: 'dynamicDocument.form.existFile',
      value: store.getNestedObservable<File[]>('dto', 'templateFile'),
      files:
        templateFile.fileName.length > 0
          ? of([
              {
                id: templateFile?.fileName,
                alfresco_id: templateFile?.fileName,
                filename: templateFile?.fileName.split('.')?.[0],
                extension: templateFile?.fileName.split('.')?.[1],
                size: templateFile?.size,
              },
            ])
          : of([]),
      onFileSelect: (event: FileSelectEvent) => {
        store.setNested('dto', 'templateFileName', event.files[0].name);
        uploadTemplate?.(event.files[0])?.subscribe();
      },
      onFileRemoved: () => {
        store.setNested('dto', 'templateFileName', '');
        removeTemplate?.();
      },
      onFileDownLoad: () => {
        downloadTemplate?.();
      },
    },
  };

  const guideFile = store.get<TemplateResponse>('guideMetadata');

  const guideField: DynamicField<FileFieldProps> = {
    label: 'dynamicDocument.form.guidePick',
    variant: FieldVariant.FILE,
    description: 'dynamicDocument.form.guidePickDescription',
    isHidden: store.getNestedObservable<boolean>('dto', 'isStatic'),
    props: {
      mode: 'advanced',
      accept: '.docx,.pdf',
      multiple: false,
      existingFileHeader: false,
      existingLabel: 'dynamicDocument.form.existFile',
      value: store.getNestedObservable<File[]>('dto', 'guideFile'),
      files:
        guideFile.fileName.length > 0
          ? of([
              {
                id: guideFile?.fileName,
                alfresco_id: guideFile?.fileName,
                filename: guideFile?.fileName.split('.')?.[0],
                extension: guideFile?.fileName.split('.')?.[1],
                size: guideFile?.size,
              },
            ])
          : of([]),
      onFileSelect: (event: FileSelectEvent) => {
        store.setNested('dto', 'guideFileName', event.files[0].name);
        uploadGuide?.(event.files[0])?.subscribe();
      },
      onFileDownLoad: () => {
        downloadGuide?.();
      },
    },
  };

  const formObject = {
    title: 'Document dynamique',
    description: '',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [nameField, orderField],
          },
          {
            fields: [marketTypeField, modePassationField],
          },
          {
            fields: [statusField, isAAOField, staticField],
          },
          {
            fields: [dynaDocsModelField],
          },
          { fields: [templateField, guideField] },
        ],
      },
    ],
  };

  return formObject;
};
